import { type ComponentProps, useEffect, useRef, useState } from 'react';
import { ResponsiveContainer } from 'recharts';

type ChartContainerProps = ComponentProps<typeof ResponsiveContainer>;

/**
 * Wrapper around Recharts ResponsiveContainer that defers rendering
 * until the parent container has positive dimensions. This prevents
 * the noisy "width(-1) and height(-1)" console warnings that occur
 * when ResponsiveContainer measures before layout settles (e.g. inside
 * react-grid-layout widgets).
 */
export function ChartContainer({ children, ...props }: ChartContainerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setReady(true);
          observer.disconnect();
        }
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ width: '100%', height: '100%' }}>
      {ready && (
        <ResponsiveContainer width="100%" height="100%" {...props}>
          {children}
        </ResponsiveContainer>
      )}
    </div>
  );
}
